import { Pressable, StyleSheet, Text, View } from 'react-native';
import CardLinkAction from 'src/presentation/components/molecules/CardLinkAction';
import Colors from 'src/core/utils/Colors';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import type { ApiCustomFeaturedItem } from 'src/core/models/api/ApiFeaturedItem';
import type { FC } from 'react';
import { useFeaturedItemStore } from 'src/domain/stores/useFeaturedItemStore';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import Translations from 'src/core/utils/Translations';

type Props = {
	item: ApiCustomFeaturedItem;
	width: number;
	height: number;
};

const CustomFeaturedItem: FC<Props> = ({ item, width, height }: Props) => {
	const { open } = useFeaturedItemStore();
	const { confirmLeaving } = useLeaveAppStore();

	const openUrl = (url: string) => confirmLeaving({ url });

	const openDetail = () => open(item);
	const buttonLink = item.button_link;

	return (
		<View style={[styles.featuredCard, Styles.card.shadowSoft, { width, height }]}>
			<Pressable style={styles.featuredLayout} onPress={openDetail}>
				<View style={styles.featuredImageFrame}>
					<ThermenImage source={item.image ? { uri: item.image } : null} style={styles.featuredImage} />
				</View>
				<View style={styles.featuredContainer}>
					<View style={styles.featuredContent}>
						{item.subtitle ? <Text style={styles.featuredWorkspaceText}>{item.subtitle}</Text> : undefined}
						<Text style={styles.featuredArrangementTitle}>{item.name}</Text>
					</View>
					{item.price ? (
						<View style={styles.featuredFooter}>
							<View style={styles.featuredPriceFromWrapper}>
								<Text style={styles.featuredPricePrefixText}>{item.price_prefix ?? Translations.startingFromShort}</Text>
								{item.from_price && <Text style={styles.featuredPriceFromText}>{item.from_price}</Text>}
							</View>
							<Text style={styles.featuredPriceText}>{item.price}</Text>
						</View>
					) : undefined}
				</View>
			</Pressable>
			<View style={styles.featuredAction}>
				{buttonLink && item.button_text ? (
					<CardLinkAction label={item.button_text} onLight={true} onPress={() => openUrl(buttonLink)} />
				) : (
					<CardLinkAction label={Translations.moreInfo} onLight={true} onPress={openDetail} />
				)}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	featuredContainer: {
		paddingVertical: Dimens.padding16,
		paddingHorizontal: Dimens.padding24,
		flexGrow: Dimens.flex1,
		flexShrink: Dimens.flex0,
		justifyContent: 'space-between',
	},
	featuredWorkspaceText: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize15,
		paddingBottom: Dimens.padding10,
	},
	featuredArrangementTitle: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize22,
		paddingBottom: Dimens.padding10,
	},
	featuredPricePrefixText: {
		color: Colors.black,
		fontSize: Dimens.fontSize14,
	},
	featuredPriceFromWrapper: {
		flexDirection: 'row',
		alignItems: 'baseline',
		gap: Dimens.gap6,
	},
	featuredPriceFromText: {
		color: Colors.black,
		fontSize: Dimens.fontSize15,
		fontWeight: Dimens.fontWeight600,
		textDecorationLine: 'line-through',
		textDecorationStyle: 'solid',
	},
	featuredPriceText: {
		fontSize: Dimens.fontSize23,
		fontWeight: Dimens.fontWeight700,
		color: Colors.black,
	},
	featuredCard: {
		borderRadius: Dimens.borderRadius4,
		backgroundColor: Colors.white,
	},
	featuredLayout: {
		flex: Dimens.flex1,
		justifyContent: 'space-between',
	},
	featuredImageFrame: {
		height: Dimens.size210,
		flexShrink: Dimens.flex0,
	},
	featuredImage: {
		width: '100%',
		height: '100%',
		borderTopLeftRadius: Dimens.borderRadius4,
		borderTopRightRadius: Dimens.borderRadius4,
		backgroundColor: Colors.gray400,
	},
	featuredContent: {
		flexGrow: Dimens.flex1,
		flexShrink: Dimens.flex0,
	},
	featuredFooter: {
		gap: Dimens.gap4,
	},
	featuredAction: {
		paddingHorizontal: Dimens.padding24,
		paddingBottom: Dimens.padding24,
	},
});

export default CustomFeaturedItem;
